import { CloseButton } from '@/features/Buttons/Close/CloseButton';
import {
  GameErrorType,
  GeolocationStepsType,
  getPopupType,
  IPopup,
  PopupConfig,
  PopupPropsType,
  PopupTypes,
} from '../types';

import PopupFooter from '../components/PopupFooter';
import PopupButton from '../components/PopupButton';
import { GenericIcon } from '@/features/GenericIcon/GenericIcon';
import { getIconProductType } from '@/features/GenericIcon/types';
import { GeolocationRefreshStatusIndicator } from '@/features/Icons/GeolocationRefresh/GeolocationRefreshStatusIndicator';
import { ArrowsArrowLeft } from '@fanduel/formation-react-icons';
import {
  DYNAMIC_POPUP_TYPES,
  ERROR_TYPE,
  GEOLOCATION_STEPS,
  POPUP_TYPES,
  SECONDARY_POPUP_TYPES,
} from '../constants';

const popupsWithoutClose = [
  POPUP_TYPES.SWJ,
  POPUP_TYPES.PROVIDER,
  ERROR_TYPE.FAILED_TO_FETCH_GAME,
  ERROR_TYPE.NET_POSITION_SIMPLE,
];

const getPopupIcon = (
  type: PopupTypes,
  step?: GeolocationStepsType,
  errorType?: GameErrorType,
) => {
  if (type === DYNAMIC_POPUP_TYPES.GEOLOCATION_REFRESH) {
    return (
      <GeolocationRefreshStatusIndicator
        status={step ?? GEOLOCATION_STEPS.LOADING}
      />
    );
  }

  return (
    <GenericIcon
      product={getIconProductType()}
      type="popup"
      subtype={errorType ?? (type as GameErrorType)}
    />
  );
};

export const generatePopupConfig = (
  type: PopupTypes,
  extraConfig?: {
    errorConfig?: {
      errorType?: GameErrorType;
      errorCode?: string;
      errorMessage?: string;
      errorParameters?: string[];
      isSecondaryPopup?: boolean;
    };
    secondaryPopupCallback?: () => void;
    customConfig?: IPopup;
    /** For stepped/dynamic popups (e.g. GEOLOCATION_REFRESH), the active step. */
    step?: GeolocationStepsType;
  },
): PopupConfig => {
  const config = getPopupType(type, extraConfig?.errorConfig?.errorType);

  // For dynamic/stepped popups, merge the active step's overrides over the base props.
  const stepProps: PopupPropsType =
    extraConfig?.step && config.dynamicProps?.[extraConfig.step]
      ? { ...config.props, ...config.dynamicProps[extraConfig.step] }
      : config.props;

  const buttons =
    extraConfig?.customConfig?.props?.buttons || stepProps.buttons;
  const popupName =
    extraConfig?.customConfig?.props?.popupName || stepProps.popupName;
  const popupMessage =
    extraConfig?.customConfig?.props?.popupMessage ||
    extraConfig?.errorConfig?.errorMessage ||
    stepProps.popupMessage;

  return {
    header: {
      backButton: extraConfig?.secondaryPopupCallback ? (
        <ArrowsArrowLeft onClick={extraConfig.secondaryPopupCallback} />
      ) : null,
      closeButton:
        popupsWithoutClose.includes(type) ||
        extraConfig?.secondaryPopupCallback ? null : (
          <CloseButton parentComponent="popup" />
        ),
    },
    main: {
      icon: getPopupIcon(
        type,
        extraConfig?.step,
        extraConfig?.errorConfig?.errorType,
      ),
      popupName: popupName,

      // Prioritize customConfig props over errorConfig over default config
      popupMessage: popupMessage,
      customSize:
        extraConfig?.customConfig?.props?.customSize || stepProps.customSize,
      horizontalButtons:
        extraConfig?.customConfig?.props?.horizontalButtons ||
        stepProps.horizontalButtons,
      buttons:
        (buttons &&
          buttons.map((btn, idx) => (
            <PopupButton key={idx} idx={idx} props={{ ...btn }} />
          ))) ||
        [],
    },

    footer: config.secondary ? (
      <PopupFooter
        footerOptions={config.secondary}
        originType={type}
        errorType={
          extraConfig?.errorConfig?.errorType
            ? extraConfig?.errorConfig?.errorType
            : SECONDARY_POPUP_TYPES.GENERIC
        }
        errorCode={extraConfig?.errorConfig?.errorCode}
        errorMessage={popupMessage}
      />
    ) : null,
    ...(extraConfig?.errorConfig
      ? {
          errorData: {
            errorType: extraConfig.errorConfig.errorType,
            errorCode: extraConfig.errorConfig.errorCode,
            errorParameters: extraConfig.errorConfig.errorParameters,
          },
        }
      : {}),
  };
};
